import { StyleSheet, Text, View, type StyleProp, type ViewStyle } from "react-native";
import { useTranslation } from "react-i18next";
import {
  BANDS,
  BAND_GLYPH,
  bandLabel,
  type IntensityBand,
} from "@heraion/shared";

import type { UiTheme } from "../theme";

// The color-scale key. ONE primitive for every surface that paints a scale
// the reader must decode — volume heatmap ramps, %1RM intensity bands,
// worked/pain dots. It's fed the SAME theme.heatmapRamp / theme.intensity the
// maps consume, so the key always matches the paint — and because those ramps
// re-hue per family, the legend is what lets a value read as "low" or "max" in
// a palette the reader hasn't memorised (DS-16).
//
// Never colour-only (the a11y floor): the ramp carries worded endpoints, the
// bands carry the BAND_GLYPH (W/M/H/X) + the localised band word, the dots
// carry a word. The third member of the glyph-map + pure-label-fn family
// alongside BAND_GLYPH/bandLabel and TargetBar's ZONE_GLYPH/zoneWord.
//
// Replaces five hand-rolled legends: the ProgressBodyMap ramp, the identical
// band rows in IntensityGridView + PlanIntensityMatrix, and the two copied
// LegendDots (PainMapPicker + EntryTipModal).
//
// Three swatch shapes, decided once here (not re-invented per caller): a
// continuous RAMP is a strip of stops, an ordinal BAND is a rounded square
// holding its glyph, a categorical DOT is a circle. The shape encodes the
// kind of scale.

type Base = { theme: UiTheme; style?: StyleProp<ViewStyle> };

export type ScaleLegendProps = Base &
  (
    | {
        variant: "ramp";
        /** Ordered low→high stops — pass `theme.heatmapRamp`. */
        stops: readonly string[];
        /** Endpoint words. Default to the shared heatmap low/high keys. */
        lowLabel?: string;
        highLabel?: string;
        /** Multiplies the ramp's type + stop size for oversized canvases
         *  (the 1080px share card). Default 1 = on-screen size. */
        scale?: number;
      }
    | {
        variant: "bands";
        /** Bands to show, low→high. Defaults to the four real BANDS. */
        bands?: readonly IntensityBand[];
        /** Append the outlined "free" item (prescribed, no banded intensity). */
        includeFree?: boolean;
      }
    | {
        variant: "dots";
        items: readonly { color: string; label: string }[];
      }
  );

export function ScaleLegend(props: ScaleLegendProps) {
  const { t } = useTranslation();
  const s = makeStyles(
    props.theme,
    props.variant === "ramp" ? (props.scale ?? 1) : 1,
  );

  if (props.variant === "ramp") {
    return (
      <View style={[s.ramp, props.style]}>
        <Text style={s.rampEnd}>
          {props.lowLabel ?? t("bodymap.heatmapLow")}
        </Text>
        <View style={s.rampStops}>
          {props.stops.map((c, i) => (
            <View
              key={`${c}-${i}`}
              style={[s.rampStop, { backgroundColor: c }]}
            />
          ))}
        </View>
        <Text style={s.rampEnd}>
          {props.highLabel ?? t("bodymap.heatmapHigh")}
        </Text>
      </View>
    );
  }

  if (props.variant === "dots") {
    return (
      <View style={[s.row, props.style]}>
        {props.items.map((it, i) => (
          <View key={`${it.label}-${i}`} style={s.item}>
            <View style={[s.dot, { backgroundColor: it.color }]} />
            <Text style={s.itemText}>{it.label}</Text>
          </View>
        ))}
      </View>
    );
  }

  const bands = props.bands ?? BANDS;
  const intensity = props.theme.intensity;
  return (
    <View style={[s.row, props.style]}>
      {bands.map((band) => (
        <View key={band} style={s.item}>
          <View style={[s.swatch, { backgroundColor: intensity[band].fill }]}>
            <Text style={[s.swatchGlyph, { color: intensity[band].on }]}>
              {BAND_GLYPH[band]}
            </Text>
          </View>
          <Text style={s.itemText}>{bandLabel(t, band)}</Text>
        </View>
      ))}
      {props.includeFree ? (
        <View style={s.item}>
          <View style={[s.swatch, s.swatchFree]} />
          <Text style={s.itemText}>
            {t("mobile.preview.intensity.free", { defaultValue: "free" })}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme, rampScale: number) {
  const { palette: p, spacing: sp, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // ramp: worded endpoints hugging a strip of equal-width stops that fills
    // whatever width the surface gives it (share card, logger footer, card).
    // rampScale sizes it up for oversized canvases (the 1080px share card).
    ramp: { flexDirection: "row", alignItems: "center", gap: sp.s2 * rampScale },
    rampStops: { flex: 1, flexDirection: "row", gap: sp.s1 * rampScale },
    rampStop: { flex: 1, height: 10 * rampScale, borderRadius: r.r1 * rampScale },
    rampEnd: {
      color: p.ink3,
      // sm (not xs) — legend text is the reader's key, held at a 12pt floor
      // for reduced vision, not a secondary caption (DS-16).
      fontSize: ty.sizes.sm * rampScale,
      fontWeight: "500",
    },
    // shared wrapping row for bands + dots
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: sp.s4,
    },
    item: { flexDirection: "row", alignItems: "center", gap: sp.s2 },
    // sm floor (see rampEnd) — one legible size across all three variants.
    itemText: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "500" },
    // band: rounded square, sized to hold its glyph (the non-colour cue drawn
    // in the band's own `on` ink, so it reads on every fill).
    swatch: {
      width: 16,
      height: 16,
      borderRadius: r.r1,
      alignItems: "center",
      justifyContent: "center",
    },
    swatchGlyph: { fontSize: ty.sizes.xs, fontWeight: "800" },
    swatchFree: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: p.border,
    },
    // dot: perfect circle — categorical (worked primary/secondary, pain severity).
    // Bordered like swatchFree: a light fill (e.g. pain's yellow) measures
    // under the 3:1 non-text contrast floor against some theme surfaces with
    // no edge to anchor it — found 2026-08-16 auditing the pain-map picker's
    // first web mount (catppuccin-light: 1.26:1 unbordered).
    dot: { width: 12, height: 12, borderRadius: 6, borderWidth: 1, borderColor: p.border },
  });
}
